import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Layers, Cpu, BarChart3, Users, Workflow, Building, ShoppingCart, Heart, Truck, Shield, Zap, Database, Cloud, Bot, Brain, Target, TrendingUp, Lightbulb, Settings, Globe, Lock, Smartphone, Monitor } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS, cn } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const INDUSTRIES = [
  { id: "all", label: "Todas las Industrias", icon: Globe },
  { id: "banking", label: "Banca y Finanzas", icon: Building },
  { id: "retail", label: "Retail y E-commerce", icon: ShoppingCart },
  { id: "healthcare", label: "Salud y Ciencias de la Vida", icon: Heart },
  { id: "logistics", label: "Logística y Supply Chain", icon: Truck },
  { id: "insurance", label: "Seguros", icon: Shield },
  { id: "manufacturing", label: "Manufactura", icon: Settings },
  { id: "energy", label: "Energía y Utilities", icon: Zap },
];

const SERVICES_BY_INDUSTRY = {
  banking: [
    {
      title: "Automatización de Procesos de Crédito",
      description: "Agentes de IA que automatizan la evaluación crediticia, análisis de riesgo y aprobación de préstamos con machine learning avanzado.",
      features: ["Evaluación de riesgo en tiempo real", "Análisis de documentos automatizado", "Scoring crediticio inteligente", "Cumplimiento regulatorio automático"],
      benefits: "Reduce tiempo de aprobación en 80% y mejora precisión del análisis de riesgo en 65%",
      image: IMAGES.ANALYTICS_1,
      technologies: ["OpenAI GPT-4", "AWS Bedrock", "Salesforce Einstein"]
    },
    {
      title: "Detección de Fraudes con IA",
      description: "Sistema de monitoreo inteligente que identifica patrones sospechosos y previene fraudes en tiempo real usando algoritmos de deep learning.",
      features: ["Detección en tiempo real", "Análisis de comportamiento", "Alertas inteligentes", "Aprendizaje continuo"],
      benefits: "Reduce fraudes en 90% y falsos positivos en 70%",
      image: IMAGES.ANALYTICS_4,
      technologies: ["Microsoft Azure AI", "Google Gemini", "Anthropic Claude"]
    },
    {
      title: "Asistente Virtual Bancario",
      description: "Agente conversacional especializado en servicios bancarios que maneja consultas complejas y ejecuta transacciones de forma autónoma.",
      features: ["Procesamiento de lenguaje natural", "Integración con core bancario", "Autenticación biométrica", "Soporte multicanal"],
      benefits: "Mejora satisfacción del cliente en 85% y reduce costos operativos en 60%",
      image: IMAGES.TEAM_1,
      technologies: ["OpenAI GPT-4", "AWS Bedrock", "Microsoft Azure AI"]
    }
  ],
  retail: [
    {
      title: "Personalización Inteligente de Experiencia",
      description: "Plataforma de IA que personaliza la experiencia de compra en tiempo real basada en comportamiento, preferencias e historial del cliente.",
      features: ["Recomendaciones personalizadas", "Pricing dinámico", "Segmentación inteligente", "A/B testing automatizado"],
      benefits: "Incrementa conversiones en 45% y valor promedio de compra en 35%",
      image: IMAGES.AUTOMATION_6,
      technologies: ["Google Gemini", "AWS Bedrock", "Salesforce Einstein"]
    },
    {
      title: "Gestión Predictiva de Inventario",
      description: "Sistema de IA que predice demanda, optimiza stock y automatiza reabastecimiento usando análisis predictivo avanzado.",
      features: ["Predicción de demanda", "Optimización de stock", "Reabastecimiento automático", "Análisis de tendencias"],
      benefits: "Reduce stock obsoleto en 40% y mejora disponibilidad en 30%",
      image: IMAGES.ANALYTICS_2,
      technologies: ["Microsoft Azure AI", "OpenAI GPT-4", "Google Gemini"]
    },
    {
      title: "Chatbot de Atención al Cliente",
      description: "Agente conversacional especializado en retail que resuelve consultas, procesa devoluciones y brinda soporte técnico 24/7.",
      features: ["Soporte multiidioma", "Integración con CRM", "Procesamiento de devoluciones", "Escalamiento inteligente"],
      benefits: "Resuelve 85% de consultas automáticamente y mejora CSAT en 40%",
      image: IMAGES.TEAM_3,
      technologies: ["Anthropic Claude", "OpenAI GPT-4", "AWS Bedrock"]
    }
  ],
  healthcare: [
    {
      title: "Diagnóstico Asistido por IA",
      description: "Plataforma que asiste a médicos en diagnósticos precisos usando análisis de imágenes médicas y datos clínicos con deep learning.",
      features: ["Análisis de imágenes médicas", "Diagnóstico diferencial", "Alertas de riesgo", "Integración con HIS"],
      benefits: "Mejora precisión diagnóstica en 25% y reduce tiempo de diagnóstico en 50%",
      image: IMAGES.ANALYTICS_3,
      technologies: ["Google Gemini", "Microsoft Azure AI", "AWS Bedrock"]
    },
    {
      title: "Gestión Inteligente de Pacientes",
      description: "Sistema de IA que optimiza flujos de pacientes, programa citas y predice necesidades de recursos hospitalarios.",
      features: ["Programación inteligente", "Predicción de demanda", "Optimización de recursos", "Alertas preventivas"],
      benefits: "Reduce tiempos de espera en 35% y optimiza ocupación en 20%",
      image: IMAGES.TEAM_2,
      technologies: ["OpenAI GPT-4", "Anthropic Claude", "Microsoft Azure AI"]
    },
    {
      title: "Monitoreo Predictivo de Salud",
      description: "Agentes de IA que monitorean signos vitales en tiempo real y predicen deterioro clínico antes de que ocurra.",
      features: ["Monitoreo continuo", "Alertas tempranas", "Análisis predictivo", "Integración con dispositivos IoT"],
      benefits: "Reduce eventos adversos en 60% y mejora outcomes clínicos en 30%",
      image: IMAGES.ANALYTICS_5,
      technologies: ["AWS Bedrock", "Google Gemini", "Microsoft Azure AI"]
    }
  ],
  logistics: [
    {
      title: "Optimización de Rutas Inteligente",
      description: "Sistema de IA que optimiza rutas de entrega en tiempo real considerando tráfico, clima y restricciones operativas.",
      features: ["Optimización en tiempo real", "Análisis de tráfico", "Predicción de demoras", "Integración GPS"],
      benefits: "Reduce costos de combustible en 25% y mejora puntualidad en 40%",
      image: IMAGES.AUTOMATION_1,
      technologies: ["Google Gemini", "AWS Bedrock", "OpenAI GPT-4"]
    },
    {
      title: "Gestión Predictiva de Almacenes",
      description: "Plataforma de IA que optimiza operaciones de almacén, predice demanda y automatiza procesos de picking y packing.",
      features: ["Predicción de demanda", "Optimización de layout", "Automatización de picking", "Gestión de inventario"],
      benefits: "Incrementa eficiencia operativa en 50% y reduce errores en 70%",
      image: IMAGES.AUTOMATION_2,
      technologies: ["Microsoft Azure AI", "Anthropic Claude", "AWS Bedrock"]
    },
    {
      title: "Mantenimiento Predictivo de Flota",
      description: "Agentes de IA que monitorean vehículos en tiempo real y predicen necesidades de mantenimiento antes de fallas.",
      features: ["Monitoreo IoT", "Predicción de fallas", "Programación automática", "Análisis de costos"],
      benefits: "Reduce downtime en 45% y costos de mantenimiento en 30%",
      image: IMAGES.AUTOMATION_3,
      technologies: ["AWS Bedrock", "Google Gemini", "Microsoft Azure AI"]
    }
  ],
  insurance: [
    {
      title: "Procesamiento Inteligente de Reclamaciones",
      description: "Sistema de IA que automatiza el procesamiento de reclamaciones, evalúa daños y acelera pagos usando computer vision.",
      features: ["Evaluación automática de daños", "Procesamiento de documentos", "Detección de fraudes", "Pagos automatizados"],
      benefits: "Reduce tiempo de procesamiento en 75% y mejora satisfacción del cliente en 50%",
      image: IMAGES.ANALYTICS_6,
      technologies: ["OpenAI GPT-4", "Google Gemini", "AWS Bedrock"]
    },
    {
      title: "Suscripción y Pricing Inteligente",
      description: "Plataforma de IA que evalúa riesgos, personaliza pólizas y optimiza precios usando análisis predictivo avanzado.",
      features: ["Evaluación de riesgo", "Pricing dinámico", "Personalización de pólizas", "Análisis de competencia"],
      benefits: "Mejora rentabilidad en 35% y incrementa conversiones en 25%",
      image: IMAGES.ANALYTICS_7,
      technologies: ["Microsoft Azure AI", "Anthropic Claude", "OpenAI GPT-4"]
    },
    {
      title: "Agente Virtual de Seguros",
      description: "Asistente de IA especializado que guía clientes en selección de pólizas, procesa cambios y brinda asesoría personalizada.",
      features: ["Asesoría personalizada", "Comparación de pólizas", "Procesamiento de cambios", "Soporte 24/7"],
      benefits: "Incrementa ventas en 40% y reduce costos de servicio en 55%",
      image: IMAGES.TEAM_4,
      technologies: ["AWS Bedrock", "Google Gemini", "Microsoft Azure AI"]
    }
  ],
  manufacturing: [
    {
      title: "Control de Calidad Automatizado",
      description: "Sistema de IA con computer vision que inspecciona productos en línea de producción y detecta defectos en tiempo real.",
      features: ["Inspección visual automatizada", "Detección de defectos", "Control estadístico", "Integración con MES"],
      benefits: "Reduce defectos en 80% y mejora eficiencia de línea en 30%",
      image: IMAGES.AUTOMATION_4,
      technologies: ["Google Gemini", "Microsoft Azure AI", "AWS Bedrock"]
    },
    {
      title: "Mantenimiento Predictivo Industrial",
      description: "Plataforma de IA que monitorea equipos industriales y predice fallas antes de que ocurran usando sensores IoT.",
      features: ["Monitoreo continuo", "Predicción de fallas", "Optimización de mantenimiento", "Análisis de vibración"],
      benefits: "Reduce downtime no planificado en 60% y costos de mantenimiento en 40%",
      image: IMAGES.AUTOMATION_5,
      technologies: ["AWS Bedrock", "OpenAI GPT-4", "Microsoft Azure AI"]
    },
    {
      title: "Optimización de Procesos Productivos",
      description: "Agentes de IA que optimizan parámetros de producción en tiempo real para maximizar eficiencia y minimizar desperdicios.",
      features: ["Optimización en tiempo real", "Reducción de desperdicios", "Control de parámetros", "Análisis de eficiencia"],
      benefits: "Incrementa productividad en 25% y reduce desperdicios en 35%",
      image: IMAGES.INDUSTRIES_GRID,
      technologies: ["Anthropic Claude", "Google Gemini", "AWS Bedrock"]
    }
  ],
  energy: [
    {
      title: "Gestión Inteligente de Redes Eléctricas",
      description: "Sistema de IA que optimiza distribución eléctrica, predice demanda y gestiona recursos renovables en tiempo real.",
      features: ["Predicción de demanda", "Optimización de distribución", "Gestión de renovables", "Detección de fallas"],
      benefits: "Reduce pérdidas de energía en 20% y mejora estabilidad de red en 35%",
      image: IMAGES.ANALYTICS_8,
      technologies: ["Microsoft Azure AI", "AWS Bedrock", "Google Gemini"]
    },
    {
      title: "Mantenimiento Predictivo de Infraestructura",
      description: "Plataforma de IA que monitorea infraestructura crítica y predice necesidades de mantenimiento usando sensores avanzados.",
      features: ["Monitoreo de infraestructura", "Predicción de fallas", "Programación inteligente", "Análisis de riesgo"],
      benefits: "Reduce interrupciones en 50% y optimiza costos de mantenimiento en 30%",
      image: IMAGES.TEAM_5,
      technologies: ["OpenAI GPT-4", "Anthropic Claude", "AWS Bedrock"]
    },
    {
      title: "Optimización de Consumo Energético",
      description: "Agentes de IA que analizan patrones de consumo y optimizan uso energético en edificios e instalaciones industriales.",
      features: ["Análisis de patrones", "Optimización automática", "Alertas de eficiencia", "Integración IoT"],
      benefits: "Reduce consumo energético en 30% y costos operativos en 25%",
      image: IMAGES.TEAM_6,
      technologies: ["Google Gemini", "Microsoft Azure AI", "AWS Bedrock"]
    }
  ]
};

export default function Services() {
  const [activeIndustry, setActiveIndustry] = useState("all");

  const getServicesForIndustry = () => {
    if (activeIndustry === "all") {
      return Object.values(SERVICES_BY_INDUSTRY).flat();
    }
    return SERVICES_BY_INDUSTRY[activeIndustry as keyof typeof SERVICES_BY_INDUSTRY] || [];
  };

  const currentServices = getServicesForIndustry();

  return (
    <div className="flex flex-col w-full min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="absolute inset-0 z-0 opacity-10">
          <img 
            src={IMAGES.TECH_HERO_BG} 
            alt="Services Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10 mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-4xl"
          >
            <span className="inline-block px-4 py-1 mb-6 text-sm font-semibold tracking-wider uppercase bg-gradient-to-r from-primary to-yellow-400 text-black rounded-full">
              Servicios Especializados
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-tight">
              Soluciones de{' '}
              <span className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                IA por Industria
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
              Transformamos sectores específicos con agentes de IA especializados, automatización inteligente y análisis predictivo adaptado a los desafíos únicos de cada industria.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-primary/30"
              >
                Consulta Gratuita
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to={ROUTE_PATHS.AGENTS}
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-primary text-primary font-bold text-lg rounded-xl hover:bg-primary hover:text-black transition-all duration-300"
              >
                Ver AI Agents
                <Bot className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Filter */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="flex flex-wrap justify-center gap-3"
          >
            {INDUSTRIES.map((industry) => {
              const Icon = industry.icon;
              return (
                <button
                  key={industry.id}
                  onClick={() => setActiveIndustry(industry.id)}
                  className={cn(
                    "inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300",
                    activeIndustry === industry.id
                      ? "bg-gradient-to-r from-primary to-yellow-400 text-black shadow-lg"
                      : "bg-white text-gray-700 border border-gray-200 hover:border-primary hover:text-primary"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {industry.label}
                </button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
              {activeIndustry === "all" ? "Todos nuestros servicios" : `Servicios para ${INDUSTRIES.find(i => i.id === activeIndustry)?.label}`}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Soluciones especializadas que combinan expertise sectorial con tecnología de vanguardia para impulsar la transformación digital de tu industria.
            </p>
          </motion.div>

          <motion.div 
            key={activeIndustry}
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8"
          >
            {currentServices.map((service, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className="group bg-white rounded-3xl border border-gray-200 hover:border-primary/30 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 overflow-hidden"
              >
                {/* Service Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.slice(0, 2).map((tech, techIdx) => (
                        <span key={techIdx} className="px-2 py-1 bg-primary/90 text-black text-xs font-semibold rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-3 text-black group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-black mb-3">Características clave:</h4>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div className="p-4 bg-gradient-to-r from-primary/10 to-yellow-400/10 rounded-xl mb-6">
                    <h4 className="font-semibold text-primary mb-2">Resultados esperados:</h4>
                    <p className="text-sm text-gray-700">{service.benefits}</p>
                  </div>

                  {/* CTA */}
                  <Link 
                    to={ROUTE_PATHS.CONTACT}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-yellow-400 text-black font-bold rounded-xl hover:scale-105 transition-all duration-300"
                  >
                    Solicitar Demo
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
              Stack Tecnológico{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                Agnóstico
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trabajamos con los proveedores de LLMs más importantes del mercado, garantizando flexibilidad, escalabilidad y las mejores capacidades de IA para cada proyecto específico.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center"
          >
            {[
              { name: 'OpenAI GPT-4', desc: 'Modelos de lenguaje avanzados', icon: Brain },
              { name: 'Google Gemini', desc: 'IA multimodal de Google', icon: Lightbulb },
              { name: 'Anthropic Claude', desc: 'IA constitucional segura', icon: Shield },
              { name: 'Microsoft Azure AI', desc: 'Servicios cognitivos enterprise', icon: Cloud },
              { name: 'AWS Bedrock', desc: 'Modelos fundacionales en la nube', icon: Database }
            ].map((tech, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className="text-center group"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary/20 to-yellow-400/20 flex items-center justify-center group-hover:scale-110 transition-transform border border-primary/30">
                  <tech.icon className="w-10 h-10 text-primary" />
                </div>
                <h4 className="font-bold text-black mb-2">{tech.name}</h4>
                <p className="text-sm text-gray-600">{tech.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.INDUSTRIES_GRID} 
            alt="Background Pattern"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              ¿Listo para transformar{' '}
              <span className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                tu industria?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Agenda una consulta gratuita con nuestros expertos y descubre cómo la IA puede revolucionar tu sector específico con soluciones personalizadas y resultados medibles.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link 
                to={ROUTE_PATHS.CONTACT}
                className="group inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-xl rounded-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-primary/30"
              >
                Agenda tu Consulta Gratuita
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to={ROUTE_PATHS.AGENTS}
                className="group inline-flex items-center gap-3 px-12 py-6 border-2 border-primary text-primary font-bold text-xl rounded-xl hover:bg-primary hover:text-black transition-all duration-300"
              >
                Ver AI Agents
                <Target className="w-6 h-6 group-hover:scale-110 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}